import React, { useState, useCallback, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useSound } from '../hooks/useSound';
import { useS3Audio } from '../hooks/useS3Audio';
import { flashCorrect, flashWrong, Confetti } from '../lib/animations';
import { GameHeader, ScoreBadge, stripAnswerLeak } from '../lib/GameVisuals';
import { Search, Volume2, Eye, EyeOff, ChevronRight, Headphones, MessageCircle, Zap } from 'lucide-react';

/* ─── Tutorial Screen ─── */
function TutorialScreen({ onStart, items }) {
  const firstItem = items[0];
  const hasImages = items.some(i => (i.options || []).some(o => o.image));
  const hasDialogue = items.some(i => i.exercise_type === 'dialogue_completion');

  return (
    <div data-testid="detective-tutorial" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(180deg, #F0F4F8 0%, #E8EDF4 40%, #F5F0E8 100%)',
      padding: '2rem',
    }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Search size={48} color="#8B6914" />
          <h1 style={{ fontFamily: 'Fredoka', fontSize: '3.2rem', fontWeight: 700, color: '#1A1A2E' }}>
            LE DETECTIVE
          </h1>
        </div>
        <p style={{ fontFamily: 'Nunito', fontSize: '1.5rem', color: '#333', marginBottom: '2rem', fontWeight: 600 }}>
          Listen to the clues and solve each case!
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          {/* Card 1: Listen */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(76,110,245,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Headphones size={40} color="#4C6EF5" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#4C6EF5', marginBottom: '0.3rem' }}>
              Listen
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Hear the French description — you can replay it anytime or show the text
            </div>
          </div>

          {/* Card 2: Choose */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(12,166,120,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <MessageCircle size={40} color="#0CA678" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#0CA678', marginBottom: '0.3rem' }}>
              Choose
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              {hasImages ? 'Pick the matching image or answer from the options' : 'Pick the correct answer from the choices'}
            </div>
          </div>

          {/* Card 3: Streak */}
          <div style={{
            background: 'rgba(255,255,255,0.92)', borderRadius: '1rem', padding: '1.25rem',
            border: '2px solid rgba(245,159,0,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            <Zap size={40} color="#F59F00" style={{ marginBottom: '0.5rem' }} />
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.3rem', fontWeight: 700, color: '#F59F00', marginBottom: '0.3rem' }}>
              Streak!
            </div>
            <div style={{ fontFamily: 'Nunito', fontSize: '1.05rem', color: '#555', lineHeight: 1.3 }}>
              Solve cases in a row to build your streak!
            </div>
          </div>
        </div>

        {/* Example case */}
        {firstItem && (
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: '#555', marginBottom: '0.5rem' }}>
              Example case:
            </div>
            <div style={{
              display: 'inline-block', padding: '0.5rem 1.2rem', borderRadius: '0.75rem',
              background: 'rgba(245,159,0,0.06)', border: '2px solid rgba(245,159,0,0.15)',
              fontFamily: 'Fredoka', fontSize: '1.1rem', fontWeight: 600, color: '#555', fontStyle: 'italic',
            }}>
              "{(firstItem.audio_text_fr || '').slice(0, 80)}{(firstItem.audio_text_fr || '').length > 80 ? '...' : ''}"
            </div>
            {hasDialogue && (
              <div style={{ marginTop: '0.5rem', fontFamily: 'Nunito', fontSize: '0.9rem', color: '#888' }}>
                Includes dialogue comprehension cases
              </div>
            )}
          </div>
        )}

        <button data-testid="detective-tutorial-start" onClick={onStart} className="btn-arcade btn-primary"
          style={{ fontSize: '1.8rem', padding: '0 3rem', minHeight: 72, gap: '0.75rem', borderBottomWidth: 8 }}>
          <ChevronRight size={32} /> Start Investigating!
        </button>
      </div>
    </div>
  );
}

export function LeDetective({ items, settings, onGameEnd }) {
  const { playSound, playGameMusic, stopMusic } = useSound();
  useEffect(() => { playGameMusic('detective'); return () => stopMusic(); }, [playGameMusic, stopMusic]);
  const maxRounds = Math.min(settings.rounds, items.length);

  const [showTutorial, setShowTutorial] = useState(true);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong'
  const [correctIdx, setCorrectIdx] = useState(null); // index of correct option (shown on wrong)
  const [showTranscript, setShowTranscript] = useState(false);
  const [showConfetti, setShowConfetti] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const scoreRef = useRef(null);
  const streakRef = useRef(null);
  const endedRef = useRef(false);

  // Value refs for stale closure prevention
  const scoreValRef = useRef(0);
  const bestStreakValRef = useRef(0);
  const totalAnsweredRef = useRef(0);
  const reviewRef = useRef([]);
  scoreValRef.current = score;
  bestStreakValRef.current = bestStreak;
  totalAnsweredRef.current = totalAnswered;

  const item = items[idx];
  const { play: playS3Audio, stop: stopS3Audio, isPlaying: s3Playing } = useS3Audio();

  // Sync S3 audio playing state with local isPlaying
  useEffect(() => { setIsPlaying(s3Playing); }, [s3Playing]);

  const speakFrench = useCallback((text, itemObj) => {
    if (!text) return;
    const id = itemObj?.item_id;
    playS3Audio('detective', id, text);
  }, [playS3Audio]);

  useEffect(() => {
    if (showTutorial || !item?.audio_text_fr) return;
    stopS3Audio();
    const timer = setTimeout(() => speakFrench(item.audio_text_fr, item), 500);
    setShowTranscript(false);
    setCorrectIdx(null);
    return () => clearTimeout(timer);
  }, [idx, showTutorial]);

  const handleAnswer = useCallback((optIdx, btnEl) => {
    if (feedback || !item) return;
    stopS3Audio();
    const correct = item.options[optIdx]?.correct;
    setTotalAnswered(a => a + 1);

    if (correct) {
      playSound('correct');
      setScore(s => s + 1);
      setFeedback('correct');
      if (btnEl) flashCorrect(btnEl);
      if (scoreRef.current) gsap.fromTo(scoreRef.current, { scale: 1.4 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
      // Track for review
      const correctOpt = item.options.find(o => o.correct);
      reviewRef.current.push({
        prompt: item.audio_text_fr || item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || correctOpt?.label || '—',
        userAnswer: item.options[optIdx]?.text || item.options[optIdx]?.label || '—', wasCorrect: true,
      });
      // Streak
      setStreak(s => {
        const newStreak = s + 1;
        setBestStreak(b => Math.max(b, newStreak));
        return newStreak;
      });
      if (streakRef.current) gsap.fromTo(streakRef.current, { scale: 1.5 }, { scale: 1, duration: 0.4, ease: 'elastic.out(1,0.5)' });
    } else {
      playSound('wrong');
      setFeedback('wrong');
      if (btnEl) flashWrong(btnEl);
      // Show correct answer
      const ci = item.options.findIndex(o => o.correct);
      setCorrectIdx(ci);
      // Track for review
      const correctOpt = item.options.find(o => o.correct);
      reviewRef.current.push({
        prompt: item.audio_text_fr || item.prompt_text_fr || item.prompt_text_en || '?',
        correctAnswer: correctOpt?.text || correctOpt?.label || '—',
        userAnswer: item.options[optIdx]?.text || item.options[optIdx]?.label || '—', wasCorrect: false,
      });
      // Reset streak
      setStreak(0);
    }

    setTimeout(() => {
      setFeedback(null);
      setCorrectIdx(null);
      const next = idx + 1;
      if (next >= maxRounds) endGame();
      else setIdx(next);
    }, correct ? 1000 : 1500);
  }, [feedback, item, idx, maxRounds, playSound]);

  const endGame = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    stopS3Audio();
    setShowConfetti(c => c + 1);
    playSound('win');
    const finalScore = scoreValRef.current;
    const finalTotal = totalAnsweredRef.current;
    const finalBestStreak = bestStreakValRef.current;
    setTimeout(() => onGameEnd({
      team1Score: finalScore,
      team2Score: 0,
      totalQuestions: finalTotal,
      accuracy: finalTotal > 0 ? (finalScore / finalTotal) * 100 : 0,
      bestStreak: finalBestStreak,
      questionReview: (() => {
        const review = [...reviewRef.current];
        for (let i = review.length; i < maxRounds && i < items.length; i++) {
          const it = items[i]; if (!it) break;
          const correctOpt = (it.options || []).find(o => o.correct);
          review.push({ prompt: it.audio_text_fr || it.prompt_text_fr || it.prompt_text_en || '?', correctAnswer: correctOpt?.text || correctOpt?.label || '—', userAnswer: null, wasCorrect: null });
        }
        return review;
      })(),
      reviewType: 'qa',
    }), 1500);
  }, [onGameEnd, playSound]);

  // Skip-to-end: parent fires this when teacher hits "Skip" in the game menu
  useEffect(() => {
    const onForceEnd = () => endGame();
    window.addEventListener('frenchboard:end-game-now', onForceEnd);
    return () => window.removeEventListener('frenchboard:end-game-now', onForceEnd);
  }, [endGame]);

  if (!item && !showTutorial) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text-muted)', fontSize: '1.5rem', fontFamily: 'Fredoka',
      }}>
        No detective content available
      </div>
    );
  }

  const progress = maxRounds > 0 ? (idx / maxRounds) * 100 : 0;
  const isDialogue = item?.exercise_type === 'dialogue_completion';
  const hasImages = (item?.options || []).some(o => o.image);

  return (
    <div data-testid="le-detective-game" style={{
      height: '100vh', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #F0F4F8 0%, #E8EDF4 50%, #F5F0E8 100%)',
      position: 'relative',
    }}>
      <Confetti trigger={showConfetti} />

      {showTutorial && <TutorialScreen onStart={() => setShowTutorial(false)} items={items.slice(0, maxRounds)} />}

      <GameHeader title="LE DETECTIVE" icon={<Search size={24} color="#8B6914" />} color="#8B6914">
        <ScoreBadge label="SOLVED" value={`${score}/${maxRounds}`} color="var(--success)" scoreRef={scoreRef} />
        {streak >= 2 && (
          <div ref={streakRef} data-testid="detective-streak" style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: 'rgba(245,159,0,0.1)', padding: '0.2rem 0.75rem', borderRadius: '2rem',
            border: '2px solid rgba(245,159,0,0.3)',
          }}>
            <Zap size={18} color="#F59F00" fill="#F59F00" />
            <span style={{ fontFamily: 'Fredoka', fontSize: '1.2rem', fontWeight: 800, color: '#F59F00' }}>{streak}</span>
          </div>
        )}
        <div style={{ fontFamily: 'Fredoka', fontSize: '1.05rem', color: 'var(--text-muted)', fontWeight: 600 }}>
          Case {idx + 1}/{maxRounds}
        </div>
      </GameHeader>

      {/* Progress bar */}
      <div style={{ height: 5, background: 'rgba(0,0,0,0.05)' }}>
        <div style={{
          height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #8B6914, #D4A843)',
          transition: 'width 0.5s ease', borderRadius: '0 3px 3px 0',
        }} />
      </div>

      {item && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: '1rem', padding: '0.75rem 1.5rem',
        }}>
          {/* Audio controls + transcript */}
          <div data-testid="audio-panel" style={{
            display: 'flex', alignItems: 'center', gap: '1.25rem',
            background: 'rgba(255,255,255,0.95)', borderRadius: '1.5rem', padding: '1rem 1.75rem',
            border: '2px solid rgba(0,0,0,0.06)', maxWidth: 750, width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}>
            {/* Exercise type indicator */}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
              minWidth: 60,
            }}>
              {isDialogue ? (
                <MessageCircle size={36} color="#0CA678" />
              ) : (
                <Headphones size={36} color="#4C6EF5" />
              )}
              <span style={{
                fontFamily: 'Fredoka', fontSize: '0.7rem', fontWeight: 700,
                color: isDialogue ? '#0CA678' : '#4C6EF5', textTransform: 'uppercase',
              }}>
                {isDialogue ? 'Dialogue' : 'Listen'}
              </span>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '1.2rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.5rem',
                fontFamily: 'Fredoka',
              }}>
                {isDialogue ? 'Listen to the dialogue...' : 'Listen carefully...'}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button data-testid="replay-button" onClick={() => speakFrench(item.audio_text_fr, item)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1.25rem', borderRadius: '1rem',
                    background: isPlaying ? 'var(--primary)' : 'white',
                    color: isPlaying ? 'white' : 'var(--primary)',
                    border: `2px solid ${isPlaying ? 'var(--primary)' : 'rgba(76,110,245,0.2)'}`,
                    borderBottom: isPlaying ? '2px solid #3B5BDB' : '4px solid rgba(76,110,245,0.15)',
                    fontSize: '1.15rem', fontWeight: 700, fontFamily: 'Fredoka',
                    cursor: 'pointer', touchAction: 'manipulation',
                    transition: 'all 0.2s',
                  }}>
                  <Volume2 size={20} /> {isPlaying ? 'Playing...' : 'Replay'}
                </button>
                <button data-testid="transcript-toggle" onClick={() => setShowTranscript(!showTranscript)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                    padding: '0.5rem 1.25rem', borderRadius: '1rem',
                    background: showTranscript ? 'rgba(0,0,0,0.05)' : 'white',
                    color: 'var(--text-secondary)', border: '2px solid rgba(0,0,0,0.06)',
                    borderBottom: '4px solid rgba(0,0,0,0.06)',
                    fontSize: '1.15rem', fontWeight: 700, fontFamily: 'Fredoka',
                    cursor: 'pointer', touchAction: 'manipulation',
                  }}>
                  {showTranscript ? <><EyeOff size={16} /> Hide</> : <><Eye size={16} /> Show</>} Text
                </button>
              </div>
              {showTranscript && (
                <div data-testid="transcript-text" style={{
                  marginTop: '0.5rem', fontFamily: 'Fredoka', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 600,
                  color: 'var(--text-primary)', lineHeight: 1.4, fontStyle: 'italic',
                  background: 'rgba(245,159,0,0.06)', padding: '0.5rem 0.75rem', borderRadius: '0.75rem',
                }}>"{item.audio_text_fr}"</div>
              )}
              {/* English prompt for dialogue_completion */}
              {item.prompt_text_en && (
                <div style={{
                  marginTop: '0.4rem', fontFamily: 'Nunito', fontSize: '1.15rem', fontWeight: 600,
                  color: 'var(--text-secondary)',
                }}>
                  {stripAnswerLeak(item.prompt_text_en, (item.options || []).find(o => o.correct)?.text)}
                </div>
              )}
            </div>
          </div>

          {/* Options grid */}
          <div data-testid="options-grid" style={{
            display: 'grid',
            gridTemplateColumns: hasImages
              ? `repeat(${Math.min(item.options.length, 4)}, 1fr)`
              : '1fr',
            gap: '1rem', maxWidth: hasImages ? 1100 : 700, width: '100%',
          }}>
            {(item.options || []).map((opt, i) => {
              const isCorrectHighlight = feedback === 'wrong' && correctIdx === i;
              return (
                <button key={`${idx}-${i}`} data-testid={`det-opt-${i}`}
                  onClick={(e) => handleAnswer(i, e.currentTarget)} disabled={!!feedback}
                  style={{
                    padding: hasImages ? '1.25rem 1rem' : '1rem 1.5rem', borderRadius: '1.25rem',
                    background: isCorrectHighlight ? 'rgba(47,158,68,0.1)' : 'rgba(255,255,255,0.95)',
                    border: isCorrectHighlight ? '3px solid var(--success)' : '2px solid rgba(0,0,0,0.06)',
                    borderBottom: isCorrectHighlight ? '3px solid #2B8A3E' : '5px solid rgba(0,0,0,0.08)',
                    cursor: feedback ? 'default' : 'pointer', textAlign: hasImages ? 'center' : 'left',
                    touchAction: 'manipulation',
                    boxShadow: isCorrectHighlight ? '0 0 12px rgba(47,158,68,0.15)' : '0 3px 12px rgba(0,0,0,0.06)',
                    transition: 'transform 0.1s, border-color 0.3s, background 0.3s',
                    display: hasImages ? 'flex' : 'flex',
                    flexDirection: hasImages ? 'column' : 'row',
                    alignItems: 'center', gap: hasImages ? '0.5rem' : '0.75rem',
                    justifyContent: hasImages ? 'center' : 'flex-start',
                  }}
                  onMouseDown={e => { if (!feedback) e.currentTarget.style.transform = 'translateY(3px)'; }}
                  onMouseUp={e => e.currentTarget.style.transform = ''}
                >
                  {/* Option number label (top) */}
                  <div style={{
                    fontFamily: 'Fredoka', fontSize: hasImages ? '1.6rem' : '1.4rem', fontWeight: 700,
                    color: isCorrectHighlight ? 'var(--success)' : 'var(--accent)',
                    marginBottom: 0,
                    minWidth: hasImages ? 'auto' : 36,
                    flexShrink: 0,
                  }}>{opt.label_num || opt.label || String.fromCharCode(65 + i)}</div>

                  {/* Image — LARGE, fills the card */}
                  {hasImages && opt.image && (
                    <img src={process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${opt.image}` : `/assets/content/${opt.image}`} alt=""
                      style={{
                        width: '100%', maxWidth: 'clamp(140px, 16vw, 240px)',
                        height: 'clamp(140px, 16vw, 240px)',
                        objectFit: 'contain', display: 'block',
                        borderRadius: '0.75rem',
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}

                  {/* Option text — smartboard readable */}
                  <div style={{
                    fontSize: hasImages ? 'clamp(1.15rem, 2vw, 1.4rem)' : 'clamp(1.15rem, 2vw, 1.3rem)',
                    fontWeight: 700, color: isCorrectHighlight ? 'var(--success)' : 'var(--text-primary)',
                    fontFamily: 'Fredoka', lineHeight: 1.3,
                  }}>
                    {opt.text || opt.label || (opt.image ? opt.image.replace('.png', '').replace(/_/g, ' ') : '')}
                  </div>

                  {/* Correct indicator */}
                  {isCorrectHighlight && (
                    <div style={{
                      marginTop: '0.15rem', fontFamily: 'Fredoka', fontSize: '1rem',
                      fontWeight: 700, color: 'var(--success)',
                    }}>
                      Correct Answer
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
